// Phase SA-8 — Read-only hook over public.slip_autopsies for the
// signed-in user. Pulls the most recent 100 rows; SA-8 covers
// almost all real-world use cases at this size. Pagination is a
// SA-9 polish item if usage outgrows the cap.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import type { SlipAutopsyRow } from "./autopsyHistoryHelpers.ts";

const FETCH_LIMIT = 100;

export interface UseAutopsyHistoryState {
  rows: SlipAutopsyRow[];
  loading: boolean;
  refetch: () => Promise<void>;
}

export function useAutopsyHistory(): UseAutopsyHistoryState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [rows, setRows] = useState<SlipAutopsyRow[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("slip_autopsies" as never)
        .select(
          // eslint-disable-next-line max-len
          "autopsy_id, queue_id, user_id, slip_date, bookmaker, outcome, stake, returns, profit_loss, leg_count, combined_odds, process_correct_count, below_threshold_count, model_error_count, void_count, no_data_count, overall_process_verdict, autopsy_summary, created_at",
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(FETCH_LIMIT);

      if (error) {
        // Brief N01 spirit — soft-fail to empty. The page renders
        // an empty-history state which is correct for the user.
        // eslint-disable-next-line no-console
        console.warn("[useAutopsyHistory] fetch failed:", error.message);
        setRows([]);
        setLoading(false);
        return;
      }
      setRows((data ?? []) as unknown as SlipAutopsyRow[]);
      setLoading(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(
        "[useAutopsyHistory] threw:",
        e instanceof Error ? e.message : String(e),
      );
      setRows([]);
      setLoading(false);
    }
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<UseAutopsyHistoryState>(
    () => ({ rows, loading, refetch }),
    [rows, loading, refetch],
  );
}
